import { TagBadge } from "@/components/ui/Tag/TagBadge";
import type { UIAudit } from "@/packages/types";
import { cn, useTranslation } from "@/packages/utils";
import { FileText, Pencil, Tag, Zap } from "lucide-react";
import {
  overviewTallCardClassName,
  WidgetCard,
  WidgetCardSkeleton,
} from "./WidgetCard";
import { DepthTierSetting } from "./DepthTierSetting";

interface AuditMetadataCardProps {
  audit: UIAudit;
  onEditAudit?: () => void;
  onEditTags?: () => void;
  index?: number;
}

const statusColors: Record<string, string> = {
  pending: "bg-amber-400/10 text-amber-400 border-amber-400/20",
  in_progress: "bg-blue-400/10 text-blue-400 border-blue-400/20",
  submitted: "bg-purple-400/10 text-purple-400 border-purple-400/20",
  approved: "bg-emerald-400/10 text-emerald-400 border-emerald-400/20",
  rejected: "bg-red-400/10 text-red-400 border-red-400/20",
  archived: "bg-muted/10 text-muted border-border",
};

function MetaTextField({
  label,
  rawValue,
  emptyLabel,
  maxLength,
}: {
  label: string;
  rawValue?: string;
  emptyLabel: string;
  maxLength?: number;
}) {
  const trimmed = rawValue?.trim() ?? "";
  const hasValue = trimmed.length > 0;
  const displayValue =
    hasValue && maxLength && trimmed.length > maxLength
      ? `${trimmed.slice(0, maxLength)}...`
      : trimmed;

  return (
    <div>
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted">
        {label}
      </span>
      <p
        className={cn(
          "text-xs leading-relaxed mt-0.5",
          hasValue ? "text-secondary" : "text-muted italic",
        )}
      >
        {hasValue ? displayValue : emptyLabel}
      </p>
    </div>
  );
}

export function AuditMetadataCard({
  audit,
  onEditAudit,
  onEditTags,
  index,
}: AuditMetadataCardProps) {
  const { t } = useTranslation("audits");
  const tags = audit.tags ?? [];
  const visibleTags = tags.slice(0, 3);
  const hiddenTagCount = tags.length - 3;

  return (
    <WidgetCard
      size="auto"
      title={t("overview.meta.title")}
      icon={<FileText size={16} />}
      index={index}
      className={`group ${overviewTallCardClassName}`}
      action={
        onEditAudit ? (
          <button
            type="button"
            className="text-muted hover:text-secondary transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onEditAudit();
            }}
            aria-label="Edit audit"
          >
            <Pencil size={14} />
          </button>
        ) : undefined
      }
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto slim-scroll flex flex-col gap-2.5">
          {/* Status + code row */}
          <div className="flex items-center justify-between gap-2">
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded border ${statusColors[audit.status] ?? "bg-muted/10 text-muted border-border"}`}
            >
              {t(`status.${audit.status}`, { defaultValue: audit.status })}
            </span>
            <span className="text-[11px] text-muted font-mono">
              {audit.code}
            </span>
          </div>

          {/* Trigger */}
          {audit.trigger && (
            <div className="flex items-center gap-1.5">
              <Zap size={11} className="text-muted shrink-0" />
              <span className="text-[11px] text-secondary truncate">
                {audit.trigger}
              </span>
            </div>
          )}

          {/* Depth Tier */}
          <DepthTierSetting auditId={audit.id} />

          {/* Tags */}
          {tags.length > 0 ? (
            <div className="flex items-center gap-1 flex-wrap">
              <Tag size={12} className="text-muted shrink-0" />
              {visibleTags.map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color}
                  onClick={onEditTags}
                />
              ))}
              {hiddenTagCount > 0 && (
                <span className="text-[11px] text-muted">
                  {t("overview.meta.moreTags", { count: hiddenTagCount })}
                </span>
              )}
            </div>
          ) : (
            onEditTags && (
              <button
                type="button"
                onClick={onEditTags}
                className="flex items-center gap-1 text-[11px] text-muted hover:text-secondary transition-colors"
              >
                <Tag size={11} />
                {t("overview.meta.addTags")}
              </button>
            )
          )}

          <MetaTextField
            label={t("overview.meta.description")}
            rawValue={audit.description}
            emptyLabel={t("overview.meta.noDescription")}
          />

          <MetaTextField
            label={t("overview.meta.notes")}
            rawValue={audit.notes}
            emptyLabel={t("overview.meta.noNotes")}
            maxLength={200}
          />

          <MetaTextField
            label={t("overview.meta.auditorInstructions")}
            rawValue={audit.auditorRequestText}
            emptyLabel={t("overview.meta.noAuditorInstructions")}
            maxLength={200}
          />
        </div>

        {/* Created date — pinned footer, mirrors the AI card's bottom zone */}
        <div className="mt-auto pt-2.5 border-t border-border/40 flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted">
            {t("overview.meta.created")}
          </span>
          <span className="text-xs text-secondary tabular-nums">
            {new Date(audit.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    </WidgetCard>
  );
}

export function AuditMetadataCardSkeleton({ index }: { index?: number }) {
  return (
    <WidgetCardSkeleton
      size="auto"
      index={index}
      className={overviewTallCardClassName}
    />
  );
}
